import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useLoginRoutine } from 'app/hooks/use-login-routine';
import type { FacebookMe } from 'app/types/tokens';
import { http } from '../lib/axios';
import { deleteIdentity } from './identity-delete';
import { useSetAtom } from 'jotai/index';
import { isLoggingOutAtom } from 'app/atoms';
import { create, isAxiosError } from 'axios';
import type { Language } from 'app/lib/language';
import { useLoginAsPrompt } from 'app/hooks/use-login-as-prompt';
import { saveLastAuthenticationMethod } from 'app/lib/authentication-method';

export interface Identity {
  AccessToken: string;
  ExpiresIn: number;
  IdToken: string;
  RefreshToken?: string;
  TokenType: string;
}

export interface CreateTokenDTO {
  email: string;
  password: string;
}

export type IdentityProvider = 'Google' | 'Facebook' | 'Apple';

type CreateIdentityOptions = UseMutationOptions<Identity, Error, CreateTokenDTO> & {
  /** New accounts have no linked account to offer before completing signup. */
  skipLoginAsPrompt?: boolean;
};

export function useCreateIdentity({ skipLoginAsPrompt = false, ...opts }: CreateIdentityOptions = {}) {
  const loginRoutine = useLoginRoutine();
  const promptLoginAs = useLoginAsPrompt();

  return useMutation<Identity, Error, CreateTokenDTO>({
    mutationFn: (payload) =>
      http
        .post<{ AuthenticationResult: Identity }>('/authentication', payload)
        .then((res) => res.data.AuthenticationResult),
    ...opts,
    onSuccess: async (result, variables, context) => {
      await loginRoutine(result, { beforeSetLoggedIn: skipLoginAsPrompt ? undefined : promptLoginAs });
      await saveLastAuthenticationMethod('Email');
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export enum SSOResultType {
  Signup = 'signUp',
  Login = 'logIn',
}

export function socialAuthentication(
  args: Record<string, any> & {
    provider: IdentityProvider;
    token: string;
    email: string | null;
    language: Language;
    inviteCode?: string;
    userType?: string;
  },
) {
  return http
    .post<{ AuthenticationResult: Identity; type: SSOResultType }>('/Authentication/sso', args)
    .catch((error: unknown) => {
      if (
        isAxiosError(error) &&
        (error.response?.status === 401 ||
          (error.response?.status === 409 &&
            error.response.data?.message ===
              'An account already exists for this email address. Please use the log in form.'))
      ) {
        // All SSO entry points translate this message through the api-errors namespace.
        // Keep the response intact for diagnostics and leave other conflicts (e.g. unverified email) alone.
        error.message = 'Social sign-in failed';
      }
      throw error;
    });
}

export function useDeleteIdentity(opts: UseMutationOptions<null> = {}) {
  const setIsLoggingOut = useSetAtom(isLoggingOutAtom);
  return useMutation<null>({
    mutationFn: async () => {
      setIsLoggingOut(true);
      await deleteIdentity(http);
      return Promise.resolve(null);
    },
    ...opts,
  });
}

export function getFacebookIdentity(facebookAccessToken: string) {
  return create()
    .get<FacebookMe>(`https://graph.facebook.com/me?access_token=${facebookAccessToken}&fields=id,name,email,picture`)
    .then((res) => res.data);
}
